import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { noop } from 'ui-components/default-props/noop';

import { VerifyEmailModal } from '../../components/verify-email-modal';
import { LABEL_PURCHASE_BUTTON } from '../../constants/email-resend-source';
import { useGlobalModalLaunchContext } from '../../contexts/modal-context/global-modal-context';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { useContainsDemoOrder } from '../../demos/hooks/use-contains-demo-order';
import PrepayDutyPromptOnAction from '../../fulfillment-planner/duties-and-taxes/prompt-on-action';
import { useCurrentCostReviewTrackingId } from '../../fulfillment-planner/hooks/useCurrentCostReviewTrackingId';
import { useDdpCountryConfig } from '../../hooks/use-ddp-country-config';
import { useDdpEligibleCarriers } from '../../hooks/use-ddp-eligible-carriers';
import { useDdpTermsState } from '../../hooks/use-ddp-terms-state';
import { useDoFpsRequireWalletSetup } from '../../hooks/use-do-fps-require-wallet-setup';
import { useDoesCswRequireWalletSetup } from '../../hooks/use-does-csw-require-wallet-setup';
import { useModal } from '../../hooks/use-modal';
import { useIsAccountEmailVerifiedSellerSetting } from '../../hooks/use-seller-setting';
import Global from '../../store/global-selectors';
import { useTrackLabelCreationStartFunc } from '../../store/labels/tracking-events';
import { WalletRegistrationOrUpdateButton } from '../../wallet/components/wallet-registration-or-update-button';

const sellerLabelProviderIdsSelector = createSelector(
  (arrayOfFps) => arrayOfFps,
  /**
   * @param {object[]} arrayOfFps
   * @returns {string[]}
   */
  (arrayOfFps) => {
    return arrayOfFps.map((fp) => fp.sellerLabelProviderId);
  },
);

/**
 * @param {string[]|Readonly<string[]>} fulfillmentPlanIds
 * @returns {string[]|Readonly<string[]>}
 */
const useGetSelectedSellerLabelProviderIds = (fulfillmentPlanIds) => {
  const bulkFulfillmentPlans =
    useSelector((state) => Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds })) || emptyArray;

  if (fulfillmentPlanIds.length >= 1) {
    return sellerLabelProviderIdsSelector(bulkFulfillmentPlans);
  }

  return emptyArray;
};

/**
 * @param {string[]|Readonly<string[]>} fulfillmentPlanIds
 * @returns {{ fulfillmentPlanId: string, shipFromId: string }[]}
 */
const useGetWalletRegistrationSuccessPayload = (fulfillmentPlanIds) => {
  const fulfillmentPlans = useSelector((state) => {
    return Global.selectEntitiesFulfillmentPlansByIds(state, {
      fulfillmentPlanIds,
    });
  });
  const postWalletRegistrationSuccessPayload = useMemo(() => {
    return fulfillmentPlans.map(({ fulfillmentPlanId, shipFromId }) => ({
      fulfillmentPlanId,
      shipFromId,
    }));
  }, [fulfillmentPlans]);
  return postWalletRegistrationSuccessPayload;
};

/**
 * Check if any fulfillment plan is a DDP Walleted international shipment
 * Requirements:
 * - Must be a walleted provider (country-specific, e.g., UPS Wallet, FedEx Walleted, DHL Express)
 * - Must be international (not domestic)
 * - Must have SenderPrepay (DDP) selected
 * @param {string[]|Readonly<string[]>} fulfillmentPlanIds
 * @param {(sellerLabelProviderId: string|number) => boolean} isWalletedDdpBySellerLabelProviderId
 * @returns {boolean}
 */
const useHasDdpWalletedInternationalShipment = (fulfillmentPlanIds, isWalletedDdpBySellerLabelProviderId) => {
  return useSelector((state) => {
    const fulfillmentPlans = Global.selectEntitiesFulfillmentPlansByIds(state, {
      fulfillmentPlanIds,
    });

    return fulfillmentPlans.some((fp) => {
      const isDdp = fp.options?.deliveredDutyPaid ?? false;
      if (!isDdp) {
        return false;
      }

      const isInternational = fp.properties?.isInternational ?? false;
      if (!isInternational) {
        return false;
      }

      // Use country-specific walleted carrier check from useDdpEligibleCarriers
      return isWalletedDdpBySellerLabelProviderId(fp.sellerLabelProviderId);
    });
  });
};

/**
 * @param {object} props
 * @param {(object: { onClick: () => void }) => React.JSX.Element} props.children
 * @param {() => void} props.labelPurchaseCallback
 * @param {string} props.trackingId
 * @param {Readonly<object[]>?} [props.availableDutiesOptions]
 * @param {boolean} [props.canShowPrepayDutyAlert]
 * @param {string[] | Readonly<string[]>} [props.fulfillmentPlanIds]
 * @param {() => void} [props.onSelectedDutyOptionChange]
 * @param {Readonly<object>?} [props.otherShippingOptions]
 * @returns {React.JSX.Element}
 */
export const LabelPurchaseButton = ({
  children: renderButton,
  labelPurchaseCallback: labelPurchaseCallbackProp,
  trackingId,
  availableDutiesOptions = emptyArray,
  canShowPrepayDutyAlert = false,
  fulfillmentPlanIds = emptyArray,
  onSelectedDutyOptionChange = noop,
  otherShippingOptions = emptyObject,
}) => {
  const { setCurrentAnalyticsSourceTrackingId } = useCurrentCostReviewTrackingId(trackingId);
  // Use useMemo to avoid recreating the array on every render
  const fpIds = useMemo(() => [...fulfillmentPlanIds], [fulfillmentPlanIds]);
  const isDemo = useContainsDemoOrder(fpIds);
  const doesLabelNeedSetup = useDoFpsRequireWalletSetup(fpIds) && !isDemo;
  const doesRateBrowserNeedSetup = useDoesCswRequireWalletSetup();
  const needsWalletSetup = doesRateBrowserNeedSetup || doesLabelNeedSetup;

  // Get country-specific DDP configuration
  const { isDdpAvailable } = useDdpCountryConfig();
  const { isWalletedDdpBySellerLabelProviderId } = useDdpEligibleCarriers();

  const trackLabelCreationStart = useTrackLabelCreationStartFunc(fpIds, trackingId);

  const labelPurchaseCallback = useCallback(() => {
    setCurrentAnalyticsSourceTrackingId(trackingId);
    trackLabelCreationStart();
    labelPurchaseCallbackProp();
  }, [labelPurchaseCallbackProp, setCurrentAnalyticsSourceTrackingId, trackLabelCreationStart, trackingId]);

  const postWalletRegistrationSuccessPayload = useGetWalletRegistrationSuccessPayload(fulfillmentPlanIds);
  const sellerLabelProviderIds = useGetSelectedSellerLabelProviderIds(fulfillmentPlanIds);
  const isAccountEmailVerified = useIsAccountEmailVerifiedSellerSetting();

  const { close: closeUnverifiedModal, isOpen: isUnverifiedModalOpen, open: openUnverifiedModal } = useModal();

  const modalProps = useMemo(
    () => ({
      labelPurchaseCallback,
      selectedFulfillmentPlanIds: fpIds,
      postWalletRegistrationSuccessPayload,
      sellerLabelProviderIds,
    }),
    [fpIds, labelPurchaseCallback, postWalletRegistrationSuccessPayload, sellerLabelProviderIds],
  );

  const launchLabelPurchaseOrVerifyFlow = () => {
    if (!isAccountEmailVerified) {
      openUnverifiedModal();
      return;
    }
    labelPurchaseCallback();
  };

  const { isAcceptanceRequired } = useDdpTermsState();
  const { openDdpTermsModal } = useGlobalModalLaunchContext();
  const hasDdpWalletedInternationalShipment = useHasDdpWalletedInternationalShipment(fpIds, isWalletedDdpBySellerLabelProviderId);

  return (
    <PrepayDutyPromptOnAction
      availableDutiesOptions={availableDutiesOptions}
      canShowPrompt={canShowPrepayDutyAlert}
      onContinueToPurchase={launchLabelPurchaseOrVerifyFlow}
      onSelectedDutyOptionChange={onSelectedDutyOptionChange}
      otherShippingOptions={otherShippingOptions}
    >
      {({ onActionTaken }) => {
        return (
          <>
            <WalletRegistrationOrUpdateButton modalProps={modalProps} trackingId={trackingId}>
              {({ openModal: openWalletModal }) => {
                // Priority: Wallet Setup > DDP Terms > Label Purchase
                if (needsWalletSetup) {
                  return renderButton({ onClick: openWalletModal });
                }
                if (isDdpAvailable && isAcceptanceRequired && hasDdpWalletedInternationalShipment && openDdpTermsModal) {
                  return renderButton({ onClick: openDdpTermsModal });
                }
                return renderButton({ onClick: onActionTaken });
              }}
            </WalletRegistrationOrUpdateButton>
            {isUnverifiedModalOpen ? <VerifyEmailModal closeUnverifiedModal={closeUnverifiedModal} source={LABEL_PURCHASE_BUTTON} /> : null}
          </>
        );
      }}
    </PrepayDutyPromptOnAction>
  );
};
